/**
 * Recent activity feed — invitation lifecycle events, RSVPs, and payments
 * interleaved chronologically. The backend already merged + sorted the rows.
 */

import * as React from 'react'
import { Icons } from '@/src/modules/components/Icons'
import { cn } from '@/src/infrastructure/lib/utils'
import { useFormat } from '@/src/infrastructure/lib/format'
import type { ActivityRow } from '../types'

interface Props {
  rows: ActivityRow[]
}

const ICONS: Record<ActivityRow['type'], React.ComponentType<{ size?: number }>> = {
  invitation: Icons.Calendar,
  rsvp: Icons.Users,
  payment: Icons.Subscription,
}

const ICON_BG: Record<ActivityRow['type'], string> = {
  invitation: 'bg-primary/10 text-primary',
  rsvp: 'bg-emerald-500/10 text-emerald-600',
  payment: 'bg-amber-500/10 text-amber-600',
}

export function RecentActivityList({ rows }: Props) {
  const fmt = useFormat()

  if (rows.length === 0) {
    return (
      <p className="text-sm text-on-surface-variant text-center py-8">
        Nothing to show yet — your most recent invitations, RSVPs, and payments will land here.
      </p>
    )
  }

  return (
    <ul className="flex flex-col">
      {rows.map((row) => {
        const Icon = ICONS[row.type]
        return (
          <li
            key={row.id}
            className="flex items-start gap-3 py-3 border-b border-outline-variant/15 last:border-b-0"
          >
            <span className={cn('w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0', ICON_BG[row.type])}>
              <Icon size={16} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-on-surface line-clamp-2">{row.title}</p>
              <p className="text-xs text-on-surface-variant">{row.subtitle}</p>
            </div>
            <div className="text-xs text-on-surface-variant text-right whitespace-nowrap pt-1">
              {row.amount ? (
                <div className="font-semibold text-on-surface">
                  {formatAmount(row.amount.value, row.amount.currency)}
                </div>
              ) : null}
              <div>{fmt.dateTime(row.occurredAt)}</div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function formatAmount(minorUnits: number, currency: string): string {
  // Backend stores payment amount in minor units (paise for INR). Convert to
  // major units for display; round-half-up to two decimals.
  const major = minorUnits / 100
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(major)
  } catch {
    return `${currency} ${major.toLocaleString()}`
  }
}
